import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { UpdateProfileRequest, UserInterest } from '../../../models/auth.model';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-profile-form',
  imports: [ReactiveFormsModule, NgbAlert],
  templateUrl: './profile-form.component.html',
  styleUrl: './profile-form.component.scss',
})
export class ProfileFormComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);

  @Output() success = new EventEmitter<void>();
  @Output() formSubmit = new EventEmitter<void>();

  profileForm: FormGroup;
  userInterests: UserInterest[] = [];
  isLoading = false;
  isSubmitting = false;
  alert: { type: string; message: string } | null = null;

  constructor() {
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      jobTitle: ['', [Validators.required, Validators.maxLength(150)]],
      department: ['', [Validators.required, Validators.maxLength(150)]],
      selectedInterests: [[]],
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  get formValid(): boolean {
    return this.profileForm.valid;
  }

  get submitting(): boolean {
    return this.isSubmitting;
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      // Load user interests and current profile
      const [interests, profile] = await Promise.all([
        this.authService.getUserInterests().toPromise(),
        this.authService.getProfile().toPromise(),
      ]);

      this.userInterests = interests || [];

      if (profile) {
        this.profileForm.patchValue({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          jobTitle: profile.jobTitle || '',
          department: profile.department || '',
          selectedInterests: profile.selectedInterestIds || [],
        });
      }
    } catch (error) {
      logger.error('Error loading profile data:', error);
      this.showAlert('error', 'Failed to load profile data. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  onInterestChange(interestId: number, checked: boolean): void {
    const selectedInterests = this.profileForm.get('selectedInterests')?.value || [];

    if (checked) {
      if (!selectedInterests.includes(interestId)) {
        selectedInterests.push(interestId);
      }
    } else {
      const index = selectedInterests.indexOf(interestId);
      if (index > -1) {
        selectedInterests.splice(index, 1);
      }
    }

    this.profileForm.patchValue({ selectedInterests });
  }

  isInterestSelected(interestId: number): boolean {
    const selectedInterests = this.profileForm.get('selectedInterests')?.value || [];
    return selectedInterests.includes(interestId);
  }

  async onSubmit(): Promise<void> {
    this.formSubmit.emit(); // Emit submit event for parent to handle

    if (this.profileForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.alert = null;

      try {
        const formValue = this.profileForm.value;
        const updateRequest: UpdateProfileRequest = {
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          jobTitle: formValue.jobTitle,
          department: formValue.department,
          interestIds: formValue.selectedInterests || [],
        };

        await this.authService.updateProfile(updateRequest).toPromise();

        // Refresh user state to update profile completion status
        await this.authService.getUserState().toPromise();

        this.showAlert('success', 'Profile updated successfully!');

        // Emit success event after a short delay
        setTimeout(() => {
          this.success.emit();
        }, 1500);
      } catch (error) {
        logger.error('Error updating profile:', error);
        this.showAlert('error', 'Failed to update profile. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    } else {
      this.markAllFieldsAsTouched();
    }
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.profileForm.controls).forEach(key => {
      this.profileForm.get(key)?.markAsTouched();
    });
  }

  private showAlert(type: string, message: string): void {
    this.alert = { type, message };
    // Auto-hide success alerts after 5 seconds
    if (type === 'success') {
      setTimeout(() => {
        this.alert = null;
      }, 5000);
    }
  }

  closeAlert(): void {
    this.alert = null;
  }

  getFieldError(fieldName: string): string | null {
    const field = this.profileForm.get(fieldName);
    if (field && field.invalid && field.touched) {
      if (field.errors?.['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (field.errors?.['maxlength']) {
        const maxLength = field.errors['maxlength'].requiredLength;
        return `${this.getFieldDisplayName(fieldName)} cannot exceed ${maxLength} characters`;
      }
    }
    return null;
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      firstName: 'First name',
      lastName: 'Last name',
      jobTitle: 'Job title',
      department: 'Department',
    };
    return displayNames[fieldName] || fieldName;
  }
}
